import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { ConnectivityProvider } from './contexts/ConnectivityContext.jsx';
import { OfflineSyncProvider } from './contexts/OfflineSyncContext.jsx';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext.jsx';
import { ProtectedRoute } from './components/ProtectedRoute.jsx';
import GeLoader from './components/common/GeLoader.jsx';
import Header from './components/layout/Header.jsx';
import BottomNav from './components/layout/BottomNav.jsx';
import SideRail from './components/layout/SideRail.jsx';
import { ErrorBoundary } from './components/ErrorBoundary.jsx';
// Eager: small public entry pages — in the main bundle to avoid a fallback flash on first load.
import Home from './pages/Home';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword.jsx';
import NotFound from './pages/NotFound.jsx';
import Register from './pages/Register';
// Lazy: everything else → its own chunk, fetched on navigation. Keeps the initial
// bundle small for low-bandwidth users (each role only downloads pages it visits).
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile.jsx'));
const GouverneurDashboard = lazy(() => import('./pages/gouverneur/Dashboard.jsx'));
const ValidationUsers = lazy(() => import('./pages/gouverneur/ValidationUsers.jsx'));
const AdminUsers = lazy(() => import('./pages/admin/Users.jsx'));
const AdminConfiguration = lazy(() => import('./pages/admin/Configuration.jsx'));
const AdminElections = lazy(() => import('./pages/admin/Elections.jsx'));
const AdminStats = lazy(() => import('./pages/admin/AdminStats.jsx'));
const LocalitesManagement = lazy(() => import('./pages/admin/LocalitesManagement.jsx'));
const AdminLogs = lazy(() => import('./pages/admin/AdminLogs.jsx'));
const AdminJournal = lazy(() => import('./pages/admin/Journal.jsx'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports.jsx'));
const GouverneurStats = lazy(() => import('./pages/gouverneur/GouverneurStats.jsx'));
const RankingReports = lazy(() => import('./pages/reports/RankingReports.jsx'));
const UnlockRequests = lazy(() => import('./pages/gouverneur/UnlockRequests.jsx'));
const ValidationPool = lazy(() => import('./pages/gouverneur/ValidationPool.jsx'));
const RegisterGovernor = lazy(() => import('./pages/gouverneur/RegisterGovernor.jsx'));
const GovernorSupport = lazy(() => import('./pages/gouverneur/GovernorSupport.jsx'));
const GovernorSupportEmpty = lazy(() =>
  import('./pages/gouverneur/GovernorSupport.jsx').then((m) => ({ default: m.GovernorSupportEmpty }))
);
const SupportCaseDetail = lazy(() => import('./pages/support/SupportCaseDetail.jsx'));
const AdminSupportCases = lazy(() => import('./pages/admin/AdminSupportCases.jsx'));
const AdminSupportEmpty = lazy(() =>
  import('./pages/admin/AdminSupportCases.jsx').then((m) => ({ default: m.AdminSupportEmpty }))
);
const AdminCronJobs = lazy(() => import('./pages/admin/AdminCronJobs.jsx'));
const CarouselManagement = lazy(() => import('./pages/admin/CarouselManagement.jsx'));
const UserLocationsMap = lazy(() => import('./pages/admin/UserLocationsMap.jsx'));
const PaymentConfiguration = lazy(() => import('./pages/admin/PaymentConfiguration.jsx'));
const SmsConfiguration = lazy(() => import('./pages/admin/SmsConfiguration.jsx'));
const PaymentMonitoring = lazy(() => import('./pages/admin/PaymentMonitoring.jsx'));
const ParcoursUnlockAudit = lazy(() => import('./pages/admin/ParcoursUnlockAudit.jsx'));
const NotificationSettings = lazy(() => import('./pages/admin/NotificationSettings.jsx'));
const RelaisDashboard = lazy(() => import('./pages/relais/Dashboard.jsx'));
const CreateBeneficiaire = lazy(() => import('./pages/relais/CreateBeneficiaire.jsx'));
const Beneficiaires = lazy(() => import('./pages/relais/Beneficiaires.jsx'));
const Interaction = lazy(() => import('./pages/relais/Interaction.jsx'));
const BeneficiaireDetail = lazy(() => import('./pages/relais/BeneficiaireDetail.jsx'));
const ProofCapturePage = lazy(() => import('./pages/relais/ProofCapturePage.jsx'));
const RegisterRelais = lazy(() => import('./pages/relais/RegisterRelais.jsx'));
const RegisterPublicRelais = lazy(() => import('./pages/relais/RegisterPublicRelais.jsx'));
const PublicRelaisManagement = lazy(() => import('./pages/admin/PublicRelaisManagement.jsx'));
const Accompagnement = lazy(() => import('./pages/beneficiaire/Accompagnement.jsx'));
const DemandesAccompagnement = lazy(() => import('./pages/relais/DemandesAccompagnement.jsx'));
const ClassementRelais = lazy(() => import('./pages/relais/ClassementRelais.jsx'));
const DemandesFonds = lazy(() => import('./pages/parrain/DemandesFonds.jsx'));
const Stats = lazy(() => import('./pages/parrain/Stats.jsx'));
const GestionRelais = lazy(() => import('./pages/parrain/GestionRelais.jsx'));
const ParrainDashboard = lazy(() => import('./pages/parrain/Dashboard.jsx'));
const Campagnes = lazy(() => import('./pages/parrain/Campagnes.jsx'));
const CampagneDetail = lazy(() => import('./pages/parrain/CampagneDetail.jsx'));
const Rankings = lazy(() => import('./pages/parrain/Rankings.jsx'));
const SuiviBeneficiaires = lazy(() => import('./pages/parrain/SuiviBeneficiaires.jsx'));
const SuiviDetail = lazy(() => import('./pages/parrain/SuiviDetail.jsx'));
const ParrainSympaDashboard = lazy(() => import('./pages/parrain-sympa/Dashboard.jsx'));
const ParrainSympaRankings = lazy(() => import('./pages/parrain-sympa/Rankings.jsx'));
const ParrainSympaContribution = lazy(() => import('./pages/parrain-sympa/Contribution.jsx'));
const CasVerifies = lazy(() => import('./pages/parrain-sympa/CasVerifies.jsx'));
const VerificationFonds = lazy(() => import('./pages/gouverneur/VerificationFonds.jsx'));
const PaymentMethods = lazy(() => import('./pages/parrain-sympa/PaymentMethods.jsx'));
const CreateDemandeFonds = lazy(() => import('./pages/relais/CreateDemandeFonds.jsx'));
const MesDemandesFonds = lazy(() => import('./pages/relais/MesDemandesFonds.jsx'));
const Documentation = lazy(() => import('./pages/Documentation.jsx'));
const DocumentationAccess = lazy(() => import('./pages/admin/DocumentationAccess.jsx'));
const AboutMission = lazy(() => import('./pages/about/AboutMission.jsx'));
const AboutSupport = lazy(() => import('./pages/about/AboutSupport.jsx'));
const AboutVersion = lazy(() => import('./pages/about/AboutVersion.jsx'));
const NotificationPreferences = lazy(() => import('./pages/NotificationPreferences.jsx'));
import { NotificationProvider } from './contexts/NotificationContext.jsx';
import PushPromptBanner from './components/NotificationCenter/PushPromptBanner.jsx';
import { AppToastProvider } from './contexts/AppToastContext.jsx';
import Toast from './components/Toast/Toast.jsx';
import FeedbackToast from './components/Toast/FeedbackToast.jsx';
import SessionIdleGuard from './components/SessionIdleGuard.jsx';
import SyncStatusBanner from './components/offline/SyncStatusBanner.jsx';
import PhoneReverifyBanner from './components/auth/PhoneReverifyBanner.jsx';
import './App.css';

// Gentle opacity fade of the route content on each navigation so slow lazy
// loads don't hard-cut (opacity only — no transform, to avoid creating a
// containing block that would offset position:fixed modals inside pages).
function RouteTransition({ children }) {
  const location = useLocation();
  return (
    <div className="page-transition" key={location.pathname}>
      {children}
    </div>
  );
}

/** Remonte en haut à chaque changement de route (SPA) — sinon un scroll
 *  sur la liste bénéficiaires laisse la fiche détail ouverte au milieu
 *  (bouton « Ouvrir l'interaction » hors écran au téléphone). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <ConnectivityProvider>
      <OfflineSyncProvider>
      <UnsavedChangesProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AppToastProvider>
        <SessionIdleGuard />
        <NotificationProvider>
        <ScrollToTop />
        <div className="app">
          <Toast />
          <FeedbackToast />
          <Header />
          <PhoneReverifyBanner />
          <SyncStatusBanner />
          <PushPromptBanner />
          <div className="app-body">
          <SideRail />
          <main className="main-content">
            <Suspense fallback={<div className="route-fallback" role="status" aria-busy="true" aria-live="polite"><GeLoader size="page" /><span>Chargement…</span></div>}>
            <RouteTransition>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/register" element={<Register />} />
              {/* Route publique pour inscription Relais via lien d'invitation */}
              <Route path="/relais/register/:token" element={<RegisterRelais />} />
              {/* Route publique pour inscription Relais Public via invitation Admin/Gouverneur */}
              <Route path="/relais/register-public/:token" element={<RegisterPublicRelais />} />
              <Route path="/gouverneur/register/:token" element={<RegisterGovernor />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/dashboard"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']}>
                    <GouverneurDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/validation"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="validate_users">
                    <ValidationUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/unlock-requests"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="manage_unlock_requests">
                    <UnlockRequests />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/support"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="handle_support">
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <GovernorSupport />
                    </Suspense>
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <GovernorSupportEmpty />
                    </Suspense>
                  }
                />
                <Route
                  path="cases/:caseId"
                  element={
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <SupportCaseDetail backTo="/gouverneur/support" embedded />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="/gouverneur/validation-pool"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="validate_pool">
                    <ValidationPool />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/verification-fonds"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="validate_pool">
                    <VerificationFonds />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/users"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="view_users">
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/map"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']}>
                    <ErrorBoundary fallbackUrl="/admin/users">
                      <UserLocationsMap />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/users/map"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="view_map">
                    <ErrorBoundary fallbackUrl="/gouverneur/dashboard">
                      <UserLocationsMap />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/beneficiaires/create"
                element={
                  <ProtectedRoute allowedTypes={['relais']}>
                    <CreateBeneficiaire />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/dashboard"
                element={
                  <ProtectedRoute allowedTypes={['relais']}>
                    <RelaisDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/beneficiaires"
                element={
                  <ProtectedRoute allowedTypes={['relais']}>
                    <Beneficiaires />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/beneficiaires/:beneficiaireId/proof/inscription"
                element={
                  <ProtectedRoute allowedTypes={['relais']}>
                    <ProofCapturePage proofType="inscription" returnPath="/relais/beneficiaires" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/beneficiaires/:beneficiaireId"
                element={
                  <ProtectedRoute allowedTypes={['relais', 'gouverneur', 'admin']}>
                    <BeneficiaireDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/beneficiaires/:beneficiaireId/interaction"
                element={
                  <ProtectedRoute allowedTypes={['relais']}>
                    <Interaction />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain/demandes"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand', 'parrain_sympa']}>
                    <DemandesFonds />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain/dashboard"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand']}>
                    <ParrainDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain/stats"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand', 'parrain_sympa']}>
                    <Stats />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain/rankings"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand']}>
                    <Rankings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain/suivi"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand']}>
                    <SuiviBeneficiaires />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain/suivi/:beneficiaireId"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand']}>
                    <SuiviDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain-sympa/dashboard"
                element={
                  <ProtectedRoute allowedTypes={['parrain_sympa']}>
                    <ParrainSympaDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain-sympa/rankings"
                element={
                  <ProtectedRoute allowedTypes={['parrain_sympa']}>
                    <ParrainSympaRankings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain-sympa/contribution"
                element={
                  <ProtectedRoute allowedTypes={['parrain_sympa']}>
                    <ParrainSympaContribution />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain-sympa/cas-verifies"
                element={
                  <ProtectedRoute allowedTypes={['parrain_sympa']}>
                    <CasVerifies />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain-sympa/payment-methods"
                element={
                  <ProtectedRoute allowedTypes={['parrain_sympa']}>
                    <PaymentMethods />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain/relais"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand']}>
                    <GestionRelais />
                  </ProtectedRoute>
                }
              />
              {/* 4.40 (Addendum C.1): "Mes Engagements" retired — campaigns replace it. */}
              <Route path="/parrain/engagements" element={<Navigate to="/parrain/campagnes" replace />} />
              <Route
                path="/parrain/campagnes"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand']}>
                    <Campagnes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrain/campagnes/:id"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand']}>
                    <CampagneDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parrains/relais"
                element={
                  <ProtectedRoute allowedTypes={['parrain_grand']}>
                    <GestionRelais />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/demandes"
                element={
                  <ProtectedRoute allowedTypes={['relais']}>
                    <MesDemandesFonds />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/demandes-accompagnement"
                element={
                  <ProtectedRoute allowedTypes={['relais']}>
                    <DemandesAccompagnement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/classement"
                element={
                  <ProtectedRoute allowedTypes={['relais', 'gouverneur', 'admin']}>
                    <ClassementRelais />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/beneficiaire/accompagnement"
                element={
                  <ProtectedRoute allowedTypes={['beneficiaire']}>
                    <Accompagnement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/relais-publics"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <PublicRelaisManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/relais-publics"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="invite_relais_public">
                    <PublicRelaisManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relais/demandes/create"
                element={
                  <ProtectedRoute allowedTypes={['relais']}>
                    <CreateDemandeFonds />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationPreferences />
                  </ProtectedRoute>
                }
              />
              <Route path="/messages" element={<Navigate to="/dashboard" replace />} />
              {/* Routes Admin */}
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/configuration"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <AdminConfiguration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/elections"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <AdminElections />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/carousel"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <CarouselManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payment-configuration"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <PaymentConfiguration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/sms-configuration"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <SmsConfiguration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notification-settings"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <NotificationSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payment-monitoring"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <PaymentMonitoring />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/stats"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <AdminStats />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/localites"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <LocalitesManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/logs"
                element={
                  <ProtectedRoute allowedTypes={['admin', 'gouverneur']}>
                    <AdminLogs />
                  </ProtectedRoute>
                }
              />
              {/* Journal d'événements centralisé (LOGGING_SPEC §5) — admin uniquement. */}
              <Route
                path="/admin/journal"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <AdminJournal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <AdminReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/parcours-unlock-audit"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <ParcoursUnlockAudit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gouverneur/stats"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="view_stats">
                    <GouverneurStats />
                  </ProtectedRoute>
                }
              />
              {/* Rapports de classement (v4.41 Phase 3a) — gouverneur (view_reports) + admin */}
              <Route
                path="/rapports/classement"
                element={
                  <ProtectedRoute allowedTypes={['gouverneur', 'admin']} requiredGovernorPermission="view_reports">
                    <RankingReports />
                  </ProtectedRoute>
                }
              />
              {/* Documentation (PRD viewer) — auth required; per-role access
                  is enforced server-side via admin-managed setting. */}
              <Route
                path="/documentation"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <Documentation />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/documentation-access"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <DocumentationAccess />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/cron"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <AdminCronJobs />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/support-cases"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <AdminSupportCases />
                    </Suspense>
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <AdminSupportEmpty />
                    </Suspense>
                  }
                />
                <Route
                  path=":caseId"
                  element={
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <SupportCaseDetail backTo="/admin/support-cases" embedded />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="/about/mission"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <AboutMission />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about/support"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <AboutSupport />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about/support/cases/:caseId"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <SupportCaseDetail backTo="/about/support" />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about/version"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</div>}>
                      <AboutVersion />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </RouteTransition>
            </Suspense>
          </main>
          </div>
          <BottomNav />
        </div>
        </NotificationProvider>
        </AppToastProvider>
      </Router>
      </UnsavedChangesProvider>
      </OfflineSyncProvider>
      </ConnectivityProvider>
    </AuthProvider>
  );
}

export default App;
